import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import kebabCase from "lodash/kebabCase";
import { JSX } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";

import { defaultComponents } from "./FormComponents";
import { FormGeneratorProvider } from "./FormGeneratorContext";
import { FormElementRenderer } from "./renderers";

import type { FormGeneratorProps } from "./types";

const FormGenerator = <
  TCustomType extends string = never,
  Values extends FieldValues = FieldValues,
>({
  fields,
  buttons,
  onSubmit,
  validationSchema,
  formMethods,
  children,
  buttonContainerConfig,
  components = {},
}: FormGeneratorProps<TCustomType, Values>): JSX.Element => {
  const defaultMethods = useForm({
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
  });

  const methods = formMethods || defaultMethods;

  const componentMap = { ...defaultComponents, ...components };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormGeneratorProvider value={{ componentMap }}>
        <FormProvider {...methods}>
          <form
            data-testid="ds-form-generator"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <Grid container spacing={2}>
              {fields.map((element, index) => (
                <FormElementRenderer<TCustomType, Values>
                  element={element}
                  methods={methods}
                  // eslint-disable-next-line react/no-array-index-key -- form fields are a static ordered list supplied by the parent
                  key={`form-${index}-${element.type}`}
                />
              ))}
              {children && (
                <Grid container size={12}>
                  {children}
                </Grid>
              )}
              {buttons && (
                <Grid
                  container
                  size={12}
                  spacing={2}
                  justifyContent="flex-end"
                  {...buttonContainerConfig}
                >
                  {buttons.map((button) => (
                    <Grid
                      {...button.grid}
                      key={`button-${kebabCase(button.label)}`}
                    >
                      <Button
                        data-testid={`ds-form-button-${kebabCase(button.label)}`}
                        type={button.type}
                        variant={button.variant || "contained"}
                        color={button.color || "primary"}
                        onClick={button.onClick}
                        disabled={button.disabled}
                        className={button.className}
                        style={button.style}
                        sx={button.sx}
                      >
                        {button.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </FormGeneratorProvider>
    </LocalizationProvider>
  );
};

export default FormGenerator;
